import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: AuthError | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Start with loading: false so the app renders immediately.
  // Auth state will update asynchronously once getSession resolves.
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    let mounted = true;

    // Shorter timeout for faster feedback; if auth hangs, user sees logged-out state quickly.
    const AUTH_INIT_TIMEOUT_MS = 5000;

    const withTimeout = async <T,>(promise: PromiseLike<T>, label: string): Promise<T> => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      try {
        return await Promise.race([
          promise,
          new Promise<T>((_resolve, reject) => {
            timeoutId = setTimeout(() => {
              reject(new Error(`Auth operation timed out (${label}) after ${AUTH_INIT_TIMEOUT_MS}ms`));
            }, AUTH_INIT_TIMEOUT_MS);
          }),
        ]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    const initializeAuth = async () => {
      try {
        // getSession reads from localStorage first (fast), then may refresh token (slow).
        // We use a short timeout so the app doesn't hang if refresh is slow.
        const { data: { session }, error: sessionError } = await withTimeout(
          supabase.auth.getSession(),
          'getSession'
        );

        if (sessionError) {
          console.warn('AuthContext session error:', sessionError);
          if (mounted) setError(sessionError);
        }

        if (mounted) {
          setUser(session?.user ?? null);
        }
      } catch (err) {
        // Timeout or network error - treat as logged out, don't block the app.
        console.warn('Auth initialization timed out or failed, proceeding as logged out:', err);
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) setInitializing(false);
      }
    };

    initializeAuth();

    // Listen for auth changes (login, logout, token refresh).
    // This will update user state when auth events occur.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setInitializing(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setUser(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
    } catch (err) {
      setError(err as AuthError);
      throw err;
    }
  };

  const value = {
    user,
    loading: initializing,
    error,
    login,
    register,
    logout,
    resetPassword,
    updatePassword
  };

  // Render children immediately - don't block on auth initialization.
  // Components that need to wait for auth can check `loading` (which maps to initializing).
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}